/* eslint-disable import/no-duplicates */
'use client';

import { useLingui } from '@lingui/react';
import { LanguageCode } from '@prismo-io/schemas';
import type { Duration } from 'date-fns';
import addSeconds from 'date-fns/addSeconds';
import formatDistanceToNow from 'date-fns/formatDistanceToNow';
import formatDuration from 'date-fns/formatDuration';
import intervalToDuration from 'date-fns/intervalToDuration';
import isBefore from 'date-fns/isBefore';
import en from 'date-fns/locale/en-GB';
import fr from 'date-fns/locale/fr';
import { useEffect, useMemo, useState } from 'react';

type Options = {
  distance?: {
    includeSeconds?: boolean;
    addSuffix?: boolean;
  };
};

export const useCountdownDate = (endDate: Date, options: Options = {}) => {
  const now = useMemo(() => new Date(), []);
  const { i18n } = useLingui();

  const endDateWith10Seconds = useMemo(
    () => addSeconds(endDate, 10),
    [endDate]
  );

  const [duration, setDuration] = useState<Duration>(() =>
    intervalToDuration({ start: now, end: endDateWith10Seconds })
  );
  const [isEnded, setIsEnded] = useState<boolean>(false);

  const locale = useMemo(() => {
    switch (i18n.locale) {
      case LanguageCode.Enum.en:
        return en;

      default:
        return fr;
    }
  }, [i18n.locale]);

  const durationMessage = useMemo<string>(
    () =>
      formatDuration(duration, {
        locale,
      }),
    [duration, locale]
  );

  const distanceMessage = useMemo<string>(
    () =>
      formatDistanceToNow(endDateWith10Seconds, {
        locale,
        includeSeconds: true,
        addSuffix: true,
        ...options.distance,
      }),
    [endDateWith10Seconds, locale, options.distance]
  );

  useEffect(() => {
    const interval = setInterval(() => {
      const now = new Date();
      const duration = intervalToDuration({
        start: now,
        end: endDateWith10Seconds,
      });

      setDuration(duration);

      if (isBefore(endDateWith10Seconds, now)) {
        setIsEnded(true);
        clearInterval(interval);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [endDateWith10Seconds, now, setIsEnded]);

  return {
    message: {
      duration: durationMessage,
      distance: distanceMessage,
    },
    isEnded,
    duration,
  };
};
